////////////////////////////////////////////////////////////////////////////////////////////////////////////////
// encounter-map/token-data.ts — XZ token → encounterArr resolver (Phase 1 of the token data contract,
// plans/xz-token-data-contract-and-widget-slimdown.md).
// ----------------------------------------------------------------------------------------------------
// Tokens on the XZ canvas carry IDENTITY in data-* (encounter_creature_id / inventory_id /
// dropzone_*); everything else lives in the widget's canonical `encounterArr` blob and is
// resolved here by id. This replaces the render-time data-* copies (team, merchant, reach,
// gross_weight, carrying_capacity_remaining_pounds, player_permission, encounter_creature_dead)
// that went stale whenever the blob changed without a hand-written attribute patch.
//
// Every helper FALLS BACK to the element's dataset when no blob entry resolves: thumbnails
// rendered outside an XZ widget (sheet inventory grids, trade windows, PHP-rendered container
// dropzones) keep their attribute contract until their renderers are migrated.
//
// Deliberately STATELESS — lookups linear-scan the live arrays (≤ tens of entries), so there is
// no id→entry cache to invalidate when WS handlers splice fresh entry objects into the blob.
// If profiling ever shows this hot, add a keyed index ON the widget instance, invalidated on
// blob-reference + entry-count change — not module-level.
//
// Dependency rule: imports ONLY from ../shared/array-types (no encounter-map globals.d.ts
// ambients) — the websocket bundle (xz-move.ts) compiles this file under its own tsconfig.
////////////////////////////////////////////////////////////////////////////////////////////////////////////////

import {
    encounterCreatureField,
    inventoryField,
    computedValue,
    layerOf,
    reachOf,
    hasPlayerPermission,
    posCoord,
} from '../shared/array-types';
import type {
    EncounterCreatureRow,
    EncounterInventoryRow,
    EntityRow,
} from '../shared/array-types';

interface TokenEncounterArr {
    encounter_creatures_arr?: unknown;
    encounter_inventories_arr?: unknown;
    [key: string]: unknown;
}

interface EncounterProjectionHost {
    encounterArr?: TokenEncounterArr;
}

interface TokenDataWindow {
    FateTide?: { encounterCache?: Map<string, unknown> };
}

function tokenDataWindow(): TokenDataWindow {
    return window as unknown as TokenDataWindow;
}

function entriesOf<T>(collection: unknown): T[] {
    if (Array.isArray(collection)) return collection as T[];
    if (collection && typeof collection === 'object') {
        return Object.values(collection as Record<string, T>);
    }
    return [];
}

function recordOf(value: unknown): Record<string, unknown> | null {
    return value !== null && typeof value === 'object'
        ? value as Record<string, unknown>
        : null;
}

function nestedValue(value: unknown, ...keys: string[]): unknown {
    let current = value;
    for (const key of keys) {
        const record = recordOf(current);
        if (!record) return undefined;
        current = record[key];
    }
    return current;
}

function firstValue(value: unknown): unknown {
    return Array.isArray(value) ? value[0] : value;
}

/** Creature entry by id inside a retained encounter projection. */
export function creatureArrIn(
    host: EncounterProjectionHost | null | undefined,
    ecId: string | null | undefined,
): EncounterCreatureRow | null {
    if (!ecId || !host?.encounterArr) return null;
    const creatures = entriesOf<EncounterCreatureRow>(host.encounterArr.encounter_creatures_arr);
    return creatures.find(
        (c) => encounterCreatureField<string>(c?.encounter_creatures, 'encounter_creature_id') === ecId,
    ) ?? null;
}

/** Inventory entry by id inside a retained encounter projection. */
export function inventoryArrIn(
    host: EncounterProjectionHost | null | undefined,
    invId: string | null | undefined,
): EncounterInventoryRow | null {
    if (!invId || !host?.encounterArr) return null;
    const items = entriesOf<EncounterInventoryRow>(host.encounterArr.encounter_inventories_arr);
    return items.find((i) => inventoryField<string>(i, 'inventory_id') === invId) ?? null;
}

/** Blob entry for a creature token (by element or explicit id), or null. */
export function tokenCreatureArr(el: Element | null | undefined, id?: string): EncounterCreatureRow | null {
    const ecId = id ?? (el as HTMLElement | null)?.dataset?.encounter_creature_id;
    return cachedCreatureArrById(ecId);
}

/** Blob entry for an inventory token (by element or explicit id), or null. */
export function tokenInventoryArr(el: Element | null | undefined, id?: string): EncounterInventoryRow | null {
    const invId = id ?? (el as HTMLElement | null)?.dataset?.inventory_id;
    if (!invId) return null;
    for (const blob of cachedFullEncounterArrs()) {
        const found = inventoryArrIn({ encounterArr: blob }, invId);
        if (found) return found;
    }
    return null;
}

/** Blob entry for whichever identity the element carries (creature first, then inventory). */
export function tokenEntityArr(el: Element | null | undefined): EntityRow | null {
    return tokenCreatureArr(el) ?? tokenInventoryArr(el);
}

function ds(el: Element | null | undefined, key: string): string | undefined {
    return (el as HTMLElement | null)?.dataset?.[key];
}

// ────────────────────────────────────────────────────────────────────────────
// Field helpers — blob first, dataset fallback for off-map elements.
// ────────────────────────────────────────────────────────────────────────────

/** Creature team UUID ('' when unknown). */
export function tokenTeam(el: Element | null | undefined): string {
    const c = tokenCreatureArr(el);
    if (c) return encounterCreatureField<string>(c.encounter_creatures, 'team') ?? '';
    return ds(el, 'team') ?? '';
}

/**
 * Every full encounter blob currently retained by the websocket cache.
 */
function cachedFullEncounterArrs(): TokenEncounterArr[] {
    const out: TokenEncounterArr[] = [];
    const ec = tokenDataWindow().FateTide?.encounterCache;
    if (ec instanceof Map) {
        for (const blob of ec.values()) {
            const encounter = recordOf(blob);
            if (encounter?.encounter_creatures_arr) out.push(encounter);
        }
    }
    return out;
}

/**
 * Creature blob by encounter_creature_id from the live encounterArr cache (WS source of truth), or null.
 * Use when you have an id (e.g. #caster_id) but no DOM token / no specific widget — scans every cached
 * full encounter blob. Reads the blob, never a (retired) data-* attribute.
 */
export function cachedCreatureArrById(ecId: string | null | undefined): EncounterCreatureRow | null {
    if (!ecId) return null;
    for (const blob of cachedFullEncounterArrs()) {
        const c = creatureArrIn({ encounterArr: blob }, ecId);
        if (c) return c;
    }
    return null;
}

/** Team UUID for an encounter_creature_id from the live encounterArr cache ('' when unknown). */
export function teamFromCacheById(ecId: string | null | undefined): string {
    const c = cachedCreatureArrById(ecId);
    return c ? (encounterCreatureField<string>(c.encounter_creatures, 'team') ?? '') : '';
}

/** Creature dead flag. Falls back to the render-time class, then dataset. */
export function tokenIsDead(el: Element | null | undefined): boolean {
    const c = tokenCreatureArr(el);
    if (c) return !!encounterCreatureField(c.encounter_creatures, 'encounter_creature_dead');
    if (el?.classList?.contains('encounter-creature-dead')) return true;
    return ds(el, 'encounter_creature_dead') === '1';
}

/** Merchant flag (infinite give/drop capacity). */
export function tokenIsMerchant(el: Element | null | undefined): boolean {
    const c = tokenCreatureArr(el);
    if (c) return !!layerOf(c, 'merchant');
    return ds(el, 'merchant') === '1';
}

/** Creature reach in ft (default 5 — same default the renderer stamped). */
export function tokenReach(el: Element | null | undefined): number {
    const c = tokenCreatureArr(el);
    if (c) return reachOf(c) ?? 5;
    const v = parseInt(ds(el, 'reach') ?? '', 10);
    return Number.isFinite(v) ? v : 5;
}

/** Gross weight (lbs) for a creature OR inventory token. */
export function tokenGrossWeight(el: Element | null | undefined): number {
    const c = tokenCreatureArr(el);
    if (c) {
        return parseFloat(String(
            nestedValue(c, 'computed', 'creature', 'weight', 'gross_weight')
                ?? firstValue(c.gross_weight)
                ?? 0,
        )) || 0;
    }
    const i = tokenInventoryArr(el);
    if (i) {
        // v34 (R13 A3): gross stored only when ≠ total — fall back to total first.
        return parseFloat(String(
            computedValue(i, 'weight', 'gross') ?? computedValue(i, 'weight', 'total') ?? 0.01,
        )) || 0;
    }
    return parseFloat(ds(el, 'gross_weight') ?? '') || 0;
}

/** One physical unit's gross weight, including sparse-stack sibling recovery. */
export function tokenUnitGrossWeight(el: Element | null | undefined): number {
    const i = tokenInventoryArr(el);
    if (!i) return tokenGrossWeight(el);
    const quantity = Math.max(1, parseInt(String(inventoryField(i, 'item_qty') ?? 1), 10) || 1);
    const ownComputed = recordOf(i.computed);
    const ownWeight = recordOf(ownComputed?.weight);
    const ownGross = parseFloat(String(firstValue(ownWeight?.gross) ?? ''));
    const ownTotal = parseFloat(String(firstValue(ownWeight?.total) ?? ''));
    if (Number.isFinite(ownGross)) return ownGross / quantity;
    if (Number.isFinite(ownTotal)) return ownTotal / quantity;

    // A sparse stack may omit its own line total while the shared catalog default was seeded by
    // another quantity. Recover from a same-template sibling's authoritative instance total.
    const encounterArr = cachedFullEncounterArrs().find((candidate) => (
        entriesOf<EncounterInventoryRow>(candidate.encounter_inventories_arr).includes(i)
    ));
    const itemId = String(inventoryField(i, 'inventory_item_id') ?? '').toUpperCase();
    for (const sibling of entriesOf<EncounterInventoryRow>(encounterArr?.encounter_inventories_arr)) {
        if (sibling === i || String(inventoryField(sibling, 'inventory_item_id') ?? '').toUpperCase() !== itemId) continue;
        const siblingQuantity = Math.max(1, parseInt(String(inventoryField(sibling, 'item_qty') ?? 1), 10) || 1);
        const siblingWeight = recordOf(recordOf(sibling.computed)?.weight);
        const siblingGross = parseFloat(String(firstValue(siblingWeight?.gross) ?? ''));
        const siblingTotal = parseFloat(String(firstValue(siblingWeight?.total) ?? ''));
        if (Number.isFinite(siblingGross)) return siblingGross / siblingQuantity;
        if (Number.isFinite(siblingTotal)) return siblingTotal / siblingQuantity;
    }
    return tokenGrossWeight(el) / quantity;
}

/**
 * Remaining carry capacity (lbs) for a creature or container token.
 * `undefined` = unknown/uncapped — callers decide the Infinity/0 default, matching
 * their previous parseFloat(dataset)||Infinity vs ||0 conventions.
 */
export function tokenCapacityRemaining(el: Element | null | undefined): number | undefined {
    const c = tokenCreatureArr(el);
    if (c) {
        const v = nestedValue(c, 'computed', 'creature', 'capacity', 'remaining', 'total')
            ?? firstValue(c.carrying_capacity_remaining_pounds);
        const n = parseFloat(String(v ?? ''));
        return Number.isFinite(n) ? n : undefined;
    }
    const i = tokenInventoryArr(el);
    if (i) {
        const v = computedValue(i, 'weight', 'capacity_remaining');
        const n = parseFloat(String(v ?? ''));
        return Number.isFinite(n) ? n : undefined;
    }
    const n = parseFloat(ds(el, 'carrying_capacity_remaining_pounds') ?? '');
    return Number.isFinite(n) ? n : undefined;
}

/** Push/drag/lift capacity (lbs) for a creature token. Undefined means the live blob cannot prove it. */
export function tokenPushDragLiftCapacity(el: Element | null | undefined): number | undefined {
    const c = tokenCreatureArr(el);
    if (c) {
        const v = nestedValue(c, 'computed', 'creature', 'capacity', 'push_drag_lift', 'total');
        const n = parseFloat(String(v ?? ''));
        return Number.isFinite(n) ? n : undefined;
    }
    const n = parseFloat(ds(el, 'creature_push_drag_lift_capacity') ?? '');
    return Number.isFinite(n) ? n : undefined;
}

/** Viewer's player_permission MOVE gate for a creature token. */
export function tokenPlayerPermission(el: Element | null | undefined): boolean {
    const c = tokenCreatureArr(el);
    if (c) return hasPlayerPermission(c);
    return ds(el, 'player_permission') === '1';
}

// ────────────────────────────────────────────────────────────────────────────
// Position — the live grid store (Phase 2 of the token data contract).
// x/z are CANONICAL in the blob (`entry.position`, read via posCoord); data-x /
// data-z survive only as a write-only mirror for off-map consumers the blob can't
// serve: PHP-rendered map-page roster tokens (render_marker_encounter_roster_inc.php,
// no widget to resolve) and the `.encounter-creature[data-x][data-z]` CSS selector.
// Reads resolve BLOB-FIRST, dataset-fallback — so off-map tokens and persistent-AOE
// items (which have no blob entry) keep working via the attribute.
// ────────────────────────────────────────────────────────────────────────────

/** Committed grid X of a token (blob position first, dataset fallback). 0 when unknown. */
export function tokenPosX(el: Element | null | undefined, id?: string): number {
    const entry = tokenCreatureArr(el, id) ?? tokenInventoryArr(el, id);
    if (entry) return posCoord(entry, 'x_pos') ?? 0;
    return parseFloat(ds(el, 'x') ?? '') || 0;
}

/** Committed grid Z of a token (blob position first, dataset fallback). 0 when unknown. */
export function tokenPosZ(el: Element | null | undefined, id?: string): number {
    const entry = tokenCreatureArr(el, id) ?? tokenInventoryArr(el, id);
    if (entry) return posCoord(entry, 'z_pos') ?? 0;
    return parseFloat(ds(el, 'z') ?? '') || 0;
}

/** Committed grid {x, z} of a token — one blob resolve for both axes. */
export function tokenPos(el: Element | null | undefined, id?: string): { x: number; z: number } {
    const entry = tokenCreatureArr(el, id) ?? tokenInventoryArr(el, id);
    if (entry) return { x: posCoord(entry, 'x_pos') ?? 0, z: posCoord(entry, 'z_pos') ?? 0 };
    return { x: parseFloat(ds(el, 'x') ?? '') || 0, z: parseFloat(ds(el, 'z') ?? '') || 0 };
}
